import React, { useState, useEffect, useRef } from 'react';

function TimeoutImage({ src, fallbackSrc, timeout = 1000, alt, onLoad, onError, ...props }) {
  const [currentSrc, setCurrentSrc] = useState(src);
  const imgRef = useRef(null);
  const isLoadedRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    let timer = null;

    // Reset state on `src` change
    setCurrentSrc(src);
    isLoadedRef.current = false;

    // Initialize image preloader
    const preloader = new Image();

    const cleanupPreloader = () => {
      preloader.onload = null;
      preloader.onerror = null;
    };

    // Set timeout to load fallback if image takes too long
    timer = setTimeout(() => {
      if (isMounted && !isLoadedRef.current) {
        cleanupPreloader();
        setCurrentSrc(fallbackSrc);
      }
    }, timeout);

    preloader.onload = () => {
      if (!isMounted) return;
      clearTimeout(timer);
      isLoadedRef.current = true;
      setCurrentSrc(src);
    };

    preloader.onerror = (err) => {
      if (!isMounted) return;
      clearTimeout(timer);
      setCurrentSrc(fallbackSrc);
      if (typeof onError === 'function') {
        onError(err);
      }
    };

    // Start loading
    preloader.src = src;

    return () => {
      isMounted = false;
      clearTimeout(timer);
      cleanupPreloader();
    };
  }, [src, fallbackSrc, timeout, onError]);

  const handleDomLoad = (e) => {
    // Fire onLoad callback only once when DOM image completes loading
    if (typeof onLoad === 'function') {
      onLoad(e);
    }
  };

  return (
    <img
      ref={imgRef}
      src={currentSrc}
      alt={alt}
      onLoad={handleDomLoad}
      {...props}
    />
  );
}

export default TimeoutImage;